/**
 * ProjectSettingsService
 * Admin service for Project Management domain settings + per-type feature gates.
 * Route: api/projects/settings
 * Controller: ProjectSettingsController.cs
 *
 * This is not a standard CRUD entity (single-row settings + feature endpoints), so it
 * extends BaseService directly rather than CrudService.
 */
import { BaseService } from '../base/BaseService';
export class ProjectSettingsService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ProjectSettingsService.DEFAULT_SERVICE_PATH,
            basePath: ProjectSettingsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** Get current project settings (secrets masked). GET /api/projects/settings */
    async get() {
        const response = await this.getRaw();
        return response.data ?? null;
    }
    getRaw() {
        return this.http.get(this.buildUrl());
    }
    /**
     * Update project settings. Pass fresh plaintext Zoho secrets as query params to
     * (re)encrypt; omit to preserve stored values. PUT /api/projects/settings
     */
    async update(settings, secrets) {
        const response = await this.updateRaw(settings, secrets);
        return response.data ?? null;
    }
    updateRaw(settings, secrets) {
        const params = new URLSearchParams();
        if (secrets?.zohoClientSecret)
            params.set('zohoClientSecret', secrets.zohoClientSecret);
        if (secrets?.zohoRefreshToken)
            params.set('zohoRefreshToken', secrets.zohoRefreshToken);
        const qs = params.toString();
        const url = this.buildUrl() + (qs ? `?${qs}` : '');
        return this.http.put(url, settings);
    }
    /** Per-type feature gates for a project type. GET /api/projects/settings/features/{typeId} */
    async getTypeFeatures(projectTypeId) {
        const response = await this.getTypeFeaturesRaw(projectTypeId);
        return response.data ?? [];
    }
    getTypeFeaturesRaw(projectTypeId) {
        const endpoint = this.getEndpoint('typeFeatures', `features/${projectTypeId}`);
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Check whether a feature is enabled (global toggle AND per-type gate).
     * GET /api/projects/settings/feature-check?feature=...&projectTypeId=...
     */
    async isFeatureEnabled(feature, projectTypeId) {
        const params = new URLSearchParams({ feature });
        if (projectTypeId != null)
            params.set('projectTypeId', String(projectTypeId));
        const endpoint = this.getEndpoint('featureCheck', 'feature-check');
        const url = this.buildUrl(endpoint) + `?${params.toString()}`;
        const response = await this.http.get(url);
        return response.data?.enabled ?? false;
    }
}
ProjectSettingsService.DEFAULT_SERVICE_PATH = 'projects';
ProjectSettingsService.BASE_PATH = 'api/projects/settings';
//# sourceMappingURL=ProjectSettingsService.js.map